const fetchYmmJson = async (fileName, signal) => {
  const cacheBuster = Date.now();
  const response = await fetch(`/cdn/shop/files/${fileName}?v=${cacheBuster}`, { signal });

  if (!response.ok) throw new Error(`YMM: failed to fetch ${fileName}`);

  return response.json();
};

class YmmSearch {
  #makeAbort  = null;
  #modelAbort = null;

  constructor(container) {
    this.container   = container;
    this.form        = container.querySelector('.js-ymm-form');
    this.yearSelect  = container.querySelector('.js-ymm-year');
    this.makeSelect  = container.querySelector('.js-ymm-make');
    this.modelSelect = container.querySelector('.js-ymm-model');
    this.searchBtn   = container.querySelector('.js-ymm-search');
    this.clearBtn    = container.querySelector('.js-ymm-clear');

    this.#bindEvents();
    this.#init();
  }

  async #init() {
    await this.#populateYears();
    await this.#restoreFromLocalStorage();
  }

  #bindEvents() {
    this.yearSelect.addEventListener('change', () => this.#onYearChange());
    this.makeSelect.addEventListener('change', () => this.#onMakeChange());
    this.modelSelect.addEventListener('change', () => this.#updateSearchBtn());
    this.clearBtn.addEventListener('click', () => this.#onClear());
    this.form.addEventListener('submit', (e) => this.#onSubmit(e));
  }

  async #populateYears() {
    this.yearSelect.disabled = true;

    try {
      const data = await fetchYmmJson('ymm_years.json');
      const placeholder = this.yearSelect.dataset.placeholder ?? '';
      this.yearSelect.innerHTML = `<option value="">${placeholder}</option>`;

      for (const { year } of data.years ?? []) {
        const opt = document.createElement('option');
        opt.value = year;
        opt.textContent = year;
        this.yearSelect.appendChild(opt);
      }
    } catch {
      // silently fail — year select stays empty
    }

    this.yearSelect.disabled = false;
  }

  async #restoreFromLocalStorage() {
    const saved = {
      year:      localStorage.getItem('ymm-year'),
      makeId:    localStorage.getItem('ymm-make-id'),
      makeTitle: localStorage.getItem('ymm-make-title'),
      modelId:   localStorage.getItem('ymm-model-id'),
      modelTitle: localStorage.getItem('ymm-model-title')
    };

    if (!saved.year || !saved.makeId || !saved.modelId) return;

    this.yearSelect.value = saved.year;

    try {
      const makeData = await fetchYmmJson(`ymm_${saved.year}.json`);
      const makes = makeData.make ?? [];
      
      this.#populate(this.makeSelect, makes, 'id', 'title');
      this.makeSelect.disabled = false;
      this.makeSelect.value = saved.makeId;

      if (this.makeSelect.value !== saved.makeId) return;

      const modelData = await fetchYmmJson(`ymm_${saved.year}_${saved.makeId}.json`);
      const models = modelData.models ?? [];

      this.#populate(this.modelSelect, models, 'handle', 'title');
      this.modelSelect.disabled = false;
      this.modelSelect.value = saved.modelId;

      this.#updateSearchBtn();
    } catch {
      this.#onClear();
    }
  }

  async #onYearChange() {
    this.#makeAbort?.abort();
    this.#makeAbort = new AbortController();

    this.#reset(this.makeSelect);
    this.#reset(this.modelSelect);
    this.#updateSearchBtn();

    const year = this.yearSelect.value;
    if (!year) return;

    try {
      const fileName = `ymm_${year}.json`;
      const data = await fetchYmmJson(fileName, this.#makeAbort.signal);
      const makes = data.make ?? [];

      this.#populate(this.makeSelect, makes, 'id', 'title');
      this.makeSelect.disabled = makes.length === 0;
    } catch (e) {
      if (e.name !== 'AbortError') this.#reset(this.makeSelect);
    }

    this.#updateSearchBtn();
  }

  async #onMakeChange() {
    this.#modelAbort?.abort();
    this.#modelAbort = new AbortController();

    this.#reset(this.modelSelect);
    this.#updateSearchBtn();

    const year   = this.yearSelect.value;
    const makeId = this.makeSelect.value;
    if (!year || !makeId) return;

    try {
      const data = await fetchYmmJson(`ymm_${year}_${makeId}.json`, this.#modelAbort.signal);
      const models = data.models ?? [];

      this.#populate(this.modelSelect, models, 'handle', 'title');
      this.modelSelect.disabled = models.length === 0;
    } catch (e) {
      if (e.name !== 'AbortError') this.#reset(this.modelSelect);
    }

    this.#updateSearchBtn();
  }

  #onClear() {
    this.yearSelect.value = '';
    this.#reset(this.makeSelect);
    this.#reset(this.modelSelect);
    this.#updateSearchBtn();

    localStorage.removeItem('ymm-year');
    localStorage.removeItem('ymm-make-id');
    localStorage.removeItem('ymm-make-title');
    localStorage.removeItem('ymm-model-id');
    localStorage.removeItem('ymm-model-title');
  }

  #onSubmit(e) {
    e.preventDefault();

    const year       = this.yearSelect.value;
    const makeId     = this.makeSelect.value;
    const makeTitle  = this.makeSelect.selectedOptions[0]?.textContent ?? '';
    const handle     = this.modelSelect.value;
    const modelTitle = this.modelSelect.selectedOptions[0]?.textContent ?? '';

    if (!handle) return;

    const url     = `/collections/${handle}`;
    const vehicle = `${makeTitle} ${modelTitle} ${year}`;

    localStorage.setItem('vehicle', vehicle);
    localStorage.setItem('vehicle-cat', handle);
    localStorage.setItem('ymm-year', year);
    localStorage.setItem('ymm-make-id', makeId);
    localStorage.setItem('ymm-make-title', makeTitle);
    localStorage.setItem('ymm-model-id', handle);
    localStorage.setItem('ymm-model-title', modelTitle);

    window.location.href = url;
  }

  #populate(selectEl, items, valueKey, labelKey) {
    const placeholder = selectEl.dataset.placeholder ?? '';
    selectEl.innerHTML = `<option value="">${placeholder}</option>`;

    for (const item of items) {
      const opt = document.createElement('option');
      opt.value = item[valueKey];
      opt.textContent = item[labelKey];
      selectEl.appendChild(opt);
    }
  }

  #reset(selectEl) {
    const placeholder = selectEl.dataset.placeholder ?? '';
    selectEl.innerHTML = `<option value="">${placeholder}</option>`;
    selectEl.value    = '';
    selectEl.disabled = true;
  }

  #updateSearchBtn() {
    const allSelected =
      this.yearSelect.value  !== '' &&
      this.makeSelect.value  !== '' &&
      this.modelSelect.value !== '';

    this.searchBtn.disabled = !allSelected;
  }
}

document.addEventListener('DOMContentLoaded', () => {
  for (const el of document.querySelectorAll('.ymm-search')) {
    new YmmSearch(el);
  }
});
